import { useState, useCallback } from 'react';

import { useTranslation } from 'react-i18next';

import { Filter, SearchRequest } from 'core/util/Search';
import { searchAudits, getMissingProfileFieldData, getNotification } from 'directory/listings/notification-modals/api';
import {
    FIELD_TO_PROFILE_FIELD,
    MAX_PROFILE_COUNT,
    LOCATION_UID_KEY,
    LOCATION_NAME_KEY,
    TRANSLATION_NAMESPACE,
    AUDIT_LOCATION_UID_KEY,
    AUDIT_SOURCE_ID_KEY,
    DATA_FIELDS_MISSING_KEY,
    GOOGLE_PLACES_SOURCE,
} from 'directory/listings/notification-modals/constants';
import {
    LocationDataRow,
    FieldType,
    AuditEntity,
    MissingFieldData,
    NotificationDetail,
    UseModalDataResult,
    UseModalDataParams,
} from 'directory/listings/notification-modals/types';
import {
    transformMissingFieldDataToRows,
    extractFieldFromNotification,
    getTranslationPluralKey,
} from 'directory/listings/notification-modals/utils';
import { searchProfilesElastic } from 'directory/profiles/Profiles';

const getBackendFieldValue = (frontendValue: FieldType): string => {
    return FIELD_TO_PROFILE_FIELD[frontendValue] || '';
};

export const useModalData = ({
    agencyId,
    tenantId,
    notificationId,
    fieldName,
    objectIds,
}: UseModalDataParams): UseModalDataResult => {
    const { t } = useTranslation(TRANSLATION_NAMESPACE);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);
    const [tableData, setTableData] = useState<LocationDataRow[]>([]);
    const [field, setField] = useState<FieldType>('UNKNOWN');
    const [modalTitle, setModalTitle] = useState<string>('');
    const [modalTitleKey, setModalTitleKey] = useState<string>('');
    const [locationCount, setLocationCount] = useState<number>(0);

    const fetchData = useCallback(async () => {
        const hasAlertData = fieldName && objectIds && objectIds.length > 0;

        if (!agencyId || !tenantId) {
            setError(new Error(t('REQUIRED_PARAMS_ERROR')));
            return;
        }

        if (!notificationId && !hasAlertData) {
            setError(new Error(t('REQUIRED_PARAMS_ERROR')));
            return;
        }

        setLoading(true);
        setError(null);

        try {
            let extractedField: FieldType;
            let filters: Array<{ name: string; values: string[] }>;

            if (!notificationId && hasAlertData) {
                extractedField = fieldName!;
                const backendFieldValue = getBackendFieldValue(extractedField);

                filters = [
                    {
                        name: DATA_FIELDS_MISSING_KEY,
                        values: [backendFieldValue],
                    },
                    {
                        name: AUDIT_LOCATION_UID_KEY,
                        values: objectIds!,
                    },
                    {
                        name: AUDIT_SOURCE_ID_KEY,
                        values: [GOOGLE_PLACES_SOURCE],
                    },
                ];
            } else {
                // Fallback to notification API (backward compatibility)
                const notification: NotificationDetail = await getNotification(tenantId, notificationId!);

                filters = (notification.filters || []).map(({ name, values }) => ({ name, values }));
                extractedField = extractFieldFromNotification(notification.parameters);
            }

            setField(extractedField);

            const searchRequest: SearchRequest = {
                filters,
                projections: [AUDIT_LOCATION_UID_KEY],
            };

            const audits: AuditEntity[] = await searchAudits(tenantId, searchRequest);
            const locationUids = audits.map((audit) => audit.locationUid);

            if (locationUids.length === 0) {
                setTableData([]);
                const titleKey = getTranslationPluralKey('FIELD_MISSING_ON_REPUTATION', 0);

                setModalTitleKey(titleKey);
                setModalTitle(
                    t(titleKey, {
                        count: 0,
                        fieldName: t(extractedField).toLowerCase(),
                    }),
                );
                setLocationCount(0);
                setLoading(false);
                return;
            }

            const profileSearchRequest: SearchRequest = {
                count: MAX_PROFILE_COUNT,
                filters: [
                    {
                        name: LOCATION_UID_KEY,
                        values: locationUids.map(String),
                    } as Filter,
                ],
                sorts: [
                    {
                        name: LOCATION_NAME_KEY,
                        sortOrder: 1,
                    },
                ],
                projections: [LOCATION_UID_KEY, LOCATION_NAME_KEY],
            };

            const { profiles } = await searchProfilesElastic(agencyId, tenantId, profileSearchRequest, {
                skipEntityMetadataCall: true,
            });

            const profileMissingFieldRequest: SearchRequest = {
                count: MAX_PROFILE_COUNT,
                filters,
            };

            const missingFieldData: MissingFieldData[] = await getMissingProfileFieldData(
                agencyId,
                tenantId,
                profileMissingFieldRequest,
            );

            const fieldNameForField = FIELD_TO_PROFILE_FIELD[extractedField];
            const filteredMissingFieldData = fieldNameForField
                ? missingFieldData.filter((data) => data.field === fieldNameForField)
                : missingFieldData;

            const transformedData = transformMissingFieldDataToRows(filteredMissingFieldData, profiles);

            setTableData(transformedData);

            const count = transformedData.length;

            setLocationCount(count);

            const titleKey = getTranslationPluralKey('FIELD_MISSING_ON_REPUTATION', count);

            setModalTitleKey(titleKey);
            setModalTitle(
                t(titleKey, {
                    count,
                    fieldName: t(extractedField).toLowerCase(),
                }),
            );
        } catch (err) {
            setError(err instanceof Error ? err : new Error(t('UNKNOWN_ERROR')));
            console.error('Error fetching modal data:', err);
        } finally {
            setLoading(false);
        }
    }, [agencyId, tenantId, notificationId, fieldName, objectIds, t]);

    return {
        loading,
        error,
        tableData,
        field,
        modalTitle,
        modalTitleKey,
        locationCount,
        fetchData,
    };
};
